import React, { useState, useMemo } from 'react';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, Activity, RefreshCw } from 'lucide-react';
import { useAuditLogs, exportAuditLogs } from '@/api/audit-logs/use-audit-logs';
import { useUsers } from '@/api/users/use-users';
import { AuditLogQueryParams, AuditLog } from '@/models/audit';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import AuditLogDetailsDialog from '@/components/audit/AuditLogDetailsDialog';

interface AuditLogTabProps {
    customerId: string;
}

const AuditLogTab: React.FC<AuditLogTabProps> = ({ customerId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [filters, setFilters] = useState<AuditLogQueryParams>({
        customer_id: customerId,
        per_page: 20,
        page: 1,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);
    const [sortConfig, setSortConfig] = useState<{
        key: keyof AuditLog;
        direction: 'asc' | 'desc';
    }>({
        key: 'created_at',
        direction: 'desc',
    });

    // Dialog state
    const [selectedAuditLog, setSelectedAuditLog] = useState<AuditLog | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Fetch audit logs
    const { data: auditLogsResponse, isLoading, isError, refetch } = useAuditLogs(filters);
    const auditLogs = auditLogsResponse?.data || [];

    // Fetch users for the filter dropdown
    const { data: usersData } = useUsers({ per_page: 100 });
    const users = usersData?.data || [];

    const handleFilterChange = (newFilters: any) => {
        setFilters(prev => ({
            ...prev,
            ...newFilters,
            customer_id: customerId, // Always include customer_id
            page: 1, // Reset to first page when filtering
        }));
        setCurrentPage(1);
    };

    const handleRefresh = () => {
        refetch();
        enqueueSnackbar('Audit logs refreshed', { variant: 'success' });
    };

    const handleExport = async () => {
        try {
            await exportAuditLogs({
                customer_id: customerId,
                event: filters.event,
                date_from: filters.date_from,
                date_to: filters.date_to,
                user_id: filters.user_id,
            });
            enqueueSnackbar('Audit logs exported successfully', { variant: 'success' });
        } catch (error) {
            console.error('Error exporting audit logs:', error);
            enqueueSnackbar('Failed to export audit logs', { variant: 'error' });
        }
    };

    const handleRowClick = (auditLog: AuditLog) => {
        setSelectedAuditLog(auditLog);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedAuditLog(null);
    };

    const getActionColor = (action: string) => {
        switch (action.toLowerCase()) {
            case 'login_successful':
            case 'login':
            case 'logout':
                return 'bg-success/10 text-success border-success/20';
            case 'login_failed':
            case 'login attempt':
                return 'bg-destructive/10 text-destructive border-destructive/20';
            case 'customer_updated':
            case 'profile update':
            case 'account update':
                return 'bg-info/10 text-info border-info/20';
            case 'password_changed':
            case 'password change':
                return 'bg-warning/10 text-warning border-warning/20';
            case 'order_created':
            case 'order_updated':
            case 'order_cancelled':
                return 'bg-primary/10 text-primary border-primary/20';
            case 'transaction_created':
            case 'transaction_updated':
                return 'bg-primary/10 text-primary border-primary/20';
            case 'kyc_submitted':
            case 'kyc_approved':
            case 'kyc_rejected':
                return 'bg-warning/10 text-warning border-warning/20';
            default:
                return 'bg-muted text-muted-foreground border-border';
        }
    };

    const formatTimestamp = (timestamp: string) => {
        try {
            return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
        } catch (error) {
            return timestamp;
        }
    };

    const getEventDescription = (log: AuditLog) => {
        switch (log.type) {
            case 'CUSTOMER_UPDATED':
                return 'Customer Information Updated';
            case 'LOGIN_SUCCESSFUL':
                return 'Customer logged in successfully';
            case 'LOGIN_FAILED':
                return 'Failed login attempt';
            case 'PASSWORD_CHANGED':
                return 'Password was changed';
            case 'PROFILE_UPDATED':
                return 'Profile information was updated';
            default:
                return (
                    log.value_before ||
                    log.value_after ||
                    `Action: ${log.type} performed by ${log.user?.name || log.created_by || 'System'}`
                );
        }
    };

    // Define columns for the DataTable
    const columns: Column<AuditLog>[] = [
        {
            id: 'id',
            label: 'ID',
            key: 'id',
            sortable: true,
            width: '120px',
            render: (_, log) => <span className="font-mono text-sm">{log.id.slice(0, 8)}...</span>,
        },
        {
            id: 'type',
            label: 'Event',
            key: 'type',
            sortable: true,
            width: '150px',
            render: (_, log) => (
                <Badge variant="outline" className={getActionColor(log.type)}>
                    {log.type}
                </Badge>
            ),
        },
        {
            id: 'description',
            label: 'Description',
            key: 'type',
            sortable: false,
            width: '300px',
            render: (_, log) => (
                <div className="max-w-xs">
                    <p className="text-sm font-medium truncate" title={getEventDescription(log)}>
                        {getEventDescription(log)}
                    </p>
                    {log.type === 'CUSTOMER_UPDATED' && log.value_before && log.value_after && (
                        <div className="text-xs text-muted-foreground mt-1">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <span className="font-medium">Before:</span>
                                    <pre
                                        className="whitespace-pre-wrap text-xs mt-1 bg-muted p-1 rounded max-h-20 overflow-y-auto truncate"
                                        title={(() => {
                                            try {
                                                return JSON.stringify(
                                                    JSON.parse(log.value_before),
                                                    null,
                                                    2
                                                );
                                            } catch (error) {
                                                return log.value_before;
                                            }
                                        })()}
                                    >
                                        {(() => {
                                            try {
                                                const str = JSON.stringify(
                                                    JSON.parse(log.value_before),
                                                    null,
                                                    2
                                                );
                                                return str.length > 100
                                                    ? str.substring(0, 100) + '...'
                                                    : str;
                                            } catch (error) {
                                                const str = log.value_before || '';
                                                return str.length > 100
                                                    ? str.substring(0, 100) + '...'
                                                    : str;
                                            }
                                        })()}
                                    </pre>
                                </div>
                                <div>
                                    <span className="font-medium">After:</span>
                                    <pre
                                        className="whitespace-pre-wrap text-xs mt-1 bg-muted p-1 rounded max-h-20 overflow-y-auto truncate"
                                        title={(() => {
                                            try {
                                                return JSON.stringify(
                                                    JSON.parse(log.value_after),
                                                    null,
                                                    2
                                                );
                                            } catch (error) {
                                                return log.value_after;
                                            }
                                        })()}
                                    >
                                        {(() => {
                                            try {
                                                const str = JSON.stringify(
                                                    JSON.parse(log.value_after),
                                                    null,
                                                    2
                                                );
                                                return str.length > 100
                                                    ? str.substring(0, 100) + '...'
                                                    : str;
                                            } catch (error) {
                                                const str = log.value_after || '';
                                                return str.length > 100
                                                    ? str.substring(0, 100) + '...'
                                                    : str;
                                            }
                                        })()}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ),
        },
        {
            id: 'user',
            label: 'User',
            key: 'created_by',
            sortable: true,
            width: '200px',
            render: (_, log) => (
                <div>
                    <div className="font-medium">
                        {log.user?.name || log.created_by || 'System'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                        {log.user?.email || log.user_id || 'N/A'}
                    </div>
                </div>
            ),
        },
        {
            id: 'metadata',
            label: 'Details',
            key: 'metadata',
            sortable: false,
            width: '200px',
            render: (_, log) => (
                <div className="text-xs text-muted-foreground space-y-1">
                    {log.ip_address && (
                        <p className="truncate" title={log.ip_address}>
                            IP: {log.ip_address}
                        </p>
                    )}
                    {log.user_agent && (
                        <p className="truncate" title={log.user_agent}>
                            UA:{' '}
                            {log.user_agent.length > 20
                                ? log.user_agent.slice(0, 20) + '...'
                                : log.user_agent}
                        </p>
                    )}
                    {log.metadata && (
                        <p
                            className="truncate"
                            title={
                                typeof log.metadata === 'string'
                                    ? log.metadata
                                    : JSON.stringify(log.metadata)
                            }
                        >
                            Meta:{' '}
                            {(() => {
                                const str =
                                    typeof log.metadata === 'string'
                                        ? log.metadata
                                        : JSON.stringify(log.metadata);
                                return str.length > 20 ? str.slice(0, 20) + '...' : str;
                            })()}
                        </p>
                    )}
                </div>
            ),
        },
        {
            id: 'created_at',
            label: 'Timestamp',
            key: 'created_at',
            sortable: true,
            width: '180px',
            render: (_, log) => (
                <div>
                    <div className="font-medium">{formatTimestamp(log.created_at)}</div>
                    <div className="text-sm text-muted-foreground">
                        <DateTimeFormatter date={log.created_at} />
                    </div>
                </div>
            ),
        },
    ];

    // Define filter fields for the GenericDataTable
    const filterFields = useMemo(
        () => [
            {
                key: 'event',
                label: 'Event Type',
                type: 'select' as const,
                options: [
                    { value: 'all', label: 'All events' },
                    { value: 'CUSTOMER_UPDATED', label: 'Customer Updated' },
                    { value: 'LOGIN_SUCCESSFUL', label: 'Login Successful' },
                    { value: 'LOGIN_FAILED', label: 'Login Failed' },
                    { value: 'PASSWORD_CHANGED', label: 'Password Changed' },
                    { value: 'PROFILE_UPDATED', label: 'Profile Updated' },
                    { value: 'ORDER_CREATED', label: 'Order Created' },
                    { value: 'ORDER_UPDATED', label: 'Order Updated' },
                    { value: 'ORDER_CANCELLED', label: 'Order Cancelled' },
                    { value: 'TRANSACTION_CREATED', label: 'Transaction Created' },
                    { value: 'TRANSACTION_UPDATED', label: 'Transaction Updated' },
                    { value: 'KYC_SUBMITTED', label: 'KYC Submitted' },
                    { value: 'KYC_APPROVED', label: 'KYC Approved' },
                    { value: 'KYC_REJECTED', label: 'KYC Rejected' },
                ],
            },
            {
                key: 'user_id',
                label: 'User',
                type: 'select' as const,
                options: [
                    { value: 'all', label: 'All users' },
                    ...users.map(user => ({
                        value: user.id,
                        label: user.name,
                    })),
                ],
            },
            {
                key: 'date_from',
                label: 'From Date',
                type: 'date' as const,
            },
            {
                key: 'date_to',
                label: 'To Date',
                type: 'date' as const,
            },
        ],
        [users]
    );

    if (isError) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Activity className="h-12 w-12 text-destructive mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Error Loading Audit Logs</h3>
                    <p className="text-muted-foreground mb-4">Failed to load audit logs data.</p>
                    <Button onClick={() => window.location.reload()}>
                        <RefreshCw className="h-4 w-4" />
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-semibold">Audit Log</h2>
                    <p className="text-muted-foreground">Monitor customer activities and changes</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </Button>
                    <Button variant="outline" onClick={handleExport} disabled={isLoading}>
                        <Download className="h-4 w-4" />
                        Export
                    </Button>
                </div>
            </div>

            {/* Generic Data Table */}
            <GenericDataTable<AuditLog>
                data={auditLogs}
                columns={columns}
                visibleColumns={columns.map(col => col.id)}
                filters={filters}
                filterFields={filterFields}
                presets={[]}
                isLoading={isLoading}
                selectedItems={[]}
                onSelectionChange={() => {}}
                getItemId={log => log.id}
                sortConfig={sortConfig}
                onSort={key =>
                    setSortConfig(prev => ({
                        key,
                        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
                    }))
                }
                totalItems={auditLogsResponse?.meta?.total || 0}
                totalPages={auditLogsResponse?.meta?.last_page || 1}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onExport={handleExport}
                onRowClick={handleRowClick}
                onFilterChange={handleFilterChange}
                onClearFilters={() =>
                    setFilters({ customer_id: customerId, per_page: 20, page: 1 })
                }
                onLoadPreset={() => {}}
                onSavePreset={() => {}}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage="No audit logs found for this customer"
                loadingMessage="Loading audit logs..."
            />

            {/* Audit Log Details Dialog */}
            <AuditLogDetailsDialog
                isOpen={isDialogOpen}
                onClose={handleCloseDialog}
                auditLog={selectedAuditLog}
            />
        </div>
    );
};

export default AuditLogTab;
